import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Play, SlidersHorizontal } from "lucide-react";

import { curriculumService, type CoverageRow, type Progress } from "../../services/curriculumService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { StatusDot } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { TOPIC_STATES, coverageStatus, day, nextTopic, topicPlan, weeksSince } from "./coverageLogic";

/** How far the class has got with a topic, and a note on it. */
export function UpdateForm({ record, onClose, onDone }: { record: Progress; onClose: () => void; onDone: (row: Progress) => void }) {
  const [percentage, setPercentage] = useState(String(Math.round(Number(record.progress_percentage))));
  const [notes, setNotes] = useState(record.notes ?? "");
  const save = useMutation({ mutationFn: curriculumService.updateProgress, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save.mutate({ id: record.id, percentage: Number(percentage), notes: notes.trim() });
  };
  return (
    <FormModal id="coverage-update" title={`${record.topic_name}: ${record.class_name}`} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The progress could not be saved.") : undefined} saveLabel="Save" note={record.status === "completed" ? "Below 100% reopens the topic." : "At 100% the topic counts as done."} onClose={onClose} onSubmit={submit}>
      <FormSection title="How far the class has got" divided={false}>
        <Field label="Covered so far" className="sm:col-span-2" hint={`${percentage}% of the topic`}>
          <input type="range" min={0} max={100} step={5} value={percentage} onChange={(e) => setPercentage(e.target.value)} className="mt-2 w-full accent-primary-600" aria-label="Percentage covered" />
        </Field>
        <Field label="Note" optional as="div" className="sm:col-span-2">
          <AIAssistField rows={3} value={notes} onChange={(next) => setNotes(next)} placeholder="e.g. Finished adding fractions; subtracting starts on Monday. Two lessons lost to sports day." context={{ key: "curriculum.coverage_note", params: { kind: "notes", id: record.id } }} draftLabel="Write it up" draftHint="Jot what you covered first; it adds nothing you did not say." />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Marking a topic done, with what went well and what got in the way, in the teacher's own words. */
export function CompleteForm({ record, onClose, onDone }: { record: Progress; onClose: () => void; onDone: (row: Progress) => void }) {
  const [achievements, setAchievements] = useState("");
  const [challenges, setChallenges] = useState("");
  const save = useMutation({ mutationFn: curriculumService.completeTopic, onSuccess: onDone });
  return (
    <FormModal
      id="coverage-complete"
      title={`Done: ${record.topic_name}, ${record.class_name}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The topic could not be marked done.") : undefined}
      saveLabel="Mark it done"
      note="Both are optional; leaders read them when reviewing coverage."
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate({ id: record.id, achievements: achievements.trim(), challenges: challenges.trim() });
      }}
    >
      <FormSection title="Looking back" divided={false}>
        <Field label="What went well" optional as="div" className="sm:col-span-2">
          <AIAssistField rows={2} value={achievements} onChange={(next) => setAchievements(next)} placeholder="e.g. Most learners can now add unlike fractions; the fraction wall helped." context={{ key: "curriculum.coverage_note", params: { kind: "achievements", id: record.id } }} draftLabel="Write it up" draftHint="Jot it first; it adds nothing you did not say." />
        </Field>
        <Field label="What got in the way" optional as="div" className="sm:col-span-2">
          <AIAssistField rows={2} value={challenges} onChange={(next) => setChallenges(next)} placeholder="e.g. Three lessons lost to the mid-term break; few textbooks." context={{ key: "curriculum.coverage_note", params: { kind: "challenges", id: record.id } }} draftLabel="Write it up" draftHint="Jot it first; it adds nothing you did not say." />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** One class's way through one syllabus: every topic in order, where it stands, and the next step. */
export function ClassSyllabus({ row, me, onClose, onChanged }: { row: CoverageRow; me?: number; onClose: () => void; onChanged: (text: string) => void }) {
  const queryClient = useQueryClient();
  const today = todayIso();
  const topics = useQuery({ queryKey: ["coverage", "topics", row.syllabus], queryFn: () => curriculumService.getSyllabusTopics(row.syllabus) });
  const records = useQuery({ queryKey: ["coverage", "records", row.syllabus, row.class_id], queryFn: () => curriculumService.getClassProgress(row.syllabus, row.class_id) });
  const [updating, setUpdating] = useState<Progress | null>(null);
  const [completing, setCompleting] = useState<Progress | null>(null);
  const refresh = (text: string) => {
    queryClient.invalidateQueries({ queryKey: ["coverage"] });
    onChanged(text);
  };
  const start = useMutation({
    mutationFn: (topic: number) => curriculumService.startTopic({ syllabus_id: row.syllabus, class_id: row.class_id, topic_id: topic }),
    onSuccess: (record) => refresh(`${record.topic_name} started with ${record.class_name}.`),
  });
  const plan = topicPlan(topics.data ?? [], records.data ?? []);
  const next = nextTopic(plan);
  // The teacher of this subject and class may carry on a record, whoever started it.
  const mine = (record?: Progress) => Boolean(record && (row.can_record || record.teacher === me));

  return (
    <DetailsModal
      size="4xl"
      title={`${row.subject_name} · ${row.class_name}`}
      headline={{ label: row.syllabus_title, value: `${row.coverage}% covered`, note: `${row.completed} of ${row.topic_count} topics done · ${row.in_progress} under way · ${row.not_started} not started` }}
      status={coverageStatus(row)}
      facts={[["Taught by", row.teachers.join(", ") || "Nothing recorded yet"], ["Last update", row.last_updated ? new Date(row.last_updated).toLocaleString() : undefined]]}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          {row.can_record && next && (
            <button type="button" disabled={start.isPending} onClick={() => start.mutate(next.id)} className="btn btn-primary gap-1.5"><Play className="h-4 w-4" /> Start {next.name}</button>
          )}
        </>
      }
    >
      {start.isError && errorLine(start.error, "The topic could not be started.")}
      {!row.can_record && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">Coverage is recorded by the teachers of this subject and class.</p>}
      {topics.isLoading || records.isLoading ? (
        <p className="text-sm text-gray-500">Loading the topics…</p>
      ) : !plan.length ? (
        <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">This syllabus has no topics yet. They are added in Syllabi.</p>
      ) : (
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th className="sheet-rownum">#</th>
                <th>Topic</th>
                <th className="text-right">Weeks planned</th>
                <th>Covered</th>
                <th>Dates</th>
                <th>Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {plan.map(({ topic, record, state }, index) => (
                <tr key={topic.id}>
                  <td className="sheet-rownum">{index + 1}</td>
                  <td className="min-w-[12rem] whitespace-normal">
                    <span className="font-medium text-gray-900">{topic.name}</span>
                    {record?.notes && <span className="mt-0.5 block text-xs text-gray-500">{record.notes}</span>}
                  </td>
                  <td className="text-right tabular-nums text-gray-700">{topic.duration_weeks}</td>
                  <td className="min-w-[8rem]">
                    {record ? (
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100"><span className={`block h-full rounded-full ${state === "behind" ? "bg-red-500" : state === "completed" ? "bg-emerald-500" : "bg-sky-500"}`} style={{ width: `${Number(record.progress_percentage)}%` }} /></span>
                        <span className="tabular-nums text-gray-700">{Math.round(Number(record.progress_percentage))}%</span>
                      </span>
                    ) : "—"}
                  </td>
                  <td className="whitespace-nowrap text-xs tabular-nums text-gray-600">
                    {record ? (record.completion_date ? `${day(record.start_date)} → ${day(record.completion_date)}` : `From ${day(record.start_date)} · week ${weeksSince(record.start_date, today) + 1}, ${topic.duration_weeks} planned`) : ""}
                  </td>
                  <td><StatusDot {...TOPIC_STATES[state]} /></td>
                  <td className="whitespace-nowrap text-right">
                    {row.can_record && !record && (
                      <button type="button" disabled={start.isPending} onClick={() => start.mutate(topic.id)} className="btn btn-outline h-7 min-h-7 px-2 text-xs">Start</button>
                    )}
                    {mine(record) && (
                      <>
                        <button type="button" onClick={() => setUpdating(record!)} className="btn btn-outline h-7 min-h-7 gap-1 px-2 text-xs"><SlidersHorizontal className="h-3.5 w-3.5" /> Update</button>
                        {record!.status !== "completed" && (
                          <button type="button" onClick={() => setCompleting(record!)} className="btn btn-primary ml-1 h-7 min-h-7 gap-1 px-2 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Done</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {updating && <UpdateForm record={updating} onClose={() => setUpdating(null)} onDone={(saved) => { setUpdating(null); refresh(`${saved.topic_name}: ${Math.round(Number(saved.progress_percentage))}% covered with ${saved.class_name}.`); }} />}
      {completing && <CompleteForm record={completing} onClose={() => setCompleting(null)} onDone={(saved) => { setCompleting(null); refresh(`${saved.topic_name} is done with ${saved.class_name}.`); }} />}
    </DetailsModal>
  );
}
